/**
 * Placement math for the auto-mindmap, kept pure and testable.
 *
 * The layout is origin-relative (root at 0,0). The orchestrator applies a
 * single final translate so the generated map lands centered on the current
 * viewport, nudged below/right if that space is already occupied by existing
 * elements.
 */

/** Axis-aligned bounds as `[minX, minY, maxX, maxY]` (matches `getCommonBounds`). */
export type Bounds = [number, number, number, number];

/** The slice of AppState placement needs; kept minimal for testability. */
export interface ViewportState {
  width: number;
  height: number;
  scrollX: number;
  scrollY: number;
  zoom: { value: number };
}

/** Gap left between existing content and a nudged map. */
const NUDGE_GAP = 40;

/** Scene coordinate under the middle of the viewport. */
export const viewportSceneCenter = (
  state: ViewportState,
): { x: number; y: number } => ({
  x: state.width / 2 / state.zoom.value - state.scrollX,
  y: state.height / 2 / state.zoom.value - state.scrollY,
});

const boxesIntersect = (a: Bounds, b: Bounds): boolean =>
  a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];

/**
 * Translate to apply to the map so its bounding box is centered on
 * `viewportCenter`. If that centered rectangle overlaps `occupied`, the map is
 * nudged below and to the right so its top-left clears the occupied box.
 */
export const computeMapPlacement = (
  mapBounds: Bounds,
  viewportCenter: { x: number; y: number },
  occupied: Bounds | null,
): { dx: number; dy: number } => {
  const centerX = (mapBounds[0] + mapBounds[2]) / 2;
  const centerY = (mapBounds[1] + mapBounds[3]) / 2;

  let dx = viewportCenter.x - centerX;
  let dy = viewportCenter.y - centerY;

  const placed: Bounds = [
    mapBounds[0] + dx,
    mapBounds[1] + dy,
    mapBounds[2] + dx,
    mapBounds[3] + dy,
  ];

  if (occupied && boxesIntersect(placed, occupied)) {
    dx += occupied[2] + NUDGE_GAP - placed[0];
    dy += occupied[3] + NUDGE_GAP - placed[1];
  }

  return { dx, dy };
};
